// ─────────────────────────────────────────────────────────────────────────────
// graphStressSeed — deterministic synthetic graphs at arbitrary scale.
//
// EVERY NODE HERE IS SYNTHETIC AND SAYS SO. Ids begin `lab:`, records carry
// metadata.synthetic, and nothing encodes a real Rexy roster, relationship or
// file. It exists to prove the ENGINE — density, hierarchy, colour, navigation,
// picking, patching — against data that can be dialled to any size.
//
// There is no fixed global maximum and no silent reduction: ask for a hundred
// thousand nodes and you get a hundred thousand nodes, or a report of what it
// cost. A generator that quietly capped itself would make every performance
// number a lie.
//
// STAGE C/D — THE MIDDLE LAYER IS REAL NOW. The G1 shape had 72 internal system
// nodes against 4,896 leaves, so a mansion was an agent hub with a wall of files
// hanging off it. Each agent now owns 4–6 explicit systems drawn deterministically
// from eight kinds, giving the structure the engine needs:
//   domain → agent → internal systems → memory clusters → file cloud.
//
// Counts come from ranged hashes of the node's own id, never Math.random, so the
// same config produces the same graph in every process.
// ─────────────────────────────────────────────────────────────────────────────

import {
  RexyGraphEdgeRecord, RexyGraphNodeKind, RexyGraphNodeRecord, RexyGraphSnapshot, RexyTrustTier,
} from '../graphTypes';
import { hashUnit } from '../graphMath';

export interface StressConfig {
  domainCount: number;
  /** Inclusive ranges, resolved per parent by a hash of its id. */
  agentsPerDomain: [number, number];
  systemsPerAgent: [number, number];
  clustersPerAgent: [number, number];
  recordsPerCluster: [number, number];
  /** Non-containment relations per leaf, on average. */
  relationsPerLeaf: number;
  seed: string;
}

/** Integer in [lo, hi] from a stable hash of `key`. */
function hashRange(key: string, salt: string, lo: number, hi: number): number {
  if (hi <= lo) return lo;
  return lo + Math.floor(hashUnit(key, salt) * (hi - lo + 1)) % (hi - lo + 1);
}

/**
 * Named presets. MEDIUM is the default visual lab workload — NOT a product cap;
 * LARGE is a stress report that exists to produce numbers, whatever they say.
 */
export const STRESS_PRESETS: { [k: string]: StressConfig } = {
  SMALL: {
    domainCount: 9, agentsPerDomain: [3, 4], systemsPerAgent: [2, 3],
    clustersPerAgent: [3, 4], recordsPerCluster: [24, 40], relationsPerLeaf: 1.4, seed: 'small',
  },
  MEDIUM: {
    domainCount: 9, agentsPerDomain: [5, 7], systemsPerAgent: [4, 6],
    clustersPerAgent: [5, 8], recordsPerCluster: [40, 90], relationsPerLeaf: 2.0, seed: 'medium',
  },
  LARGE: {
    domainCount: 12, agentsPerDomain: [8, 10], systemsPerAgent: [5, 7],
    clustersPerAgent: [8, 11], recordsPerCluster: [90, 140], relationsPerLeaf: 2.4, seed: 'large',
  },
};

export const LAB_DOMAIN_SLUGS = [
  'homepage', 'trading', 'personal', 'business', 'research',
  'growth', 'frontier', 'code', 'security',
];

export const LAB_CITY_HALL_ID = 'lab:city-hall:rexy';

const LEAF_KINDS: RexyGraphNodeKind[] = [
  'memory-record', 'file', 'document', 'thread', 'artifact', 'citation',
];
/** The mansion's engines and archives. A model is INSIDE an agent, never an agent. */
const SYSTEM_KINDS: RexyGraphNodeKind[] = [
  'tool', 'model', 'database', 'workflow', 'evidence', 'service', 'surface', 'memory-cluster',
];
const TRUST_TIERS: RexyTrustTier[] = ['HIGHEST', 'HIGHER', 'HIGH', 'MEDIUM', 'LOW'];

function pick<T>(list: T[], id: string, salt: string): T {
  return list[Math.floor(hashUnit(id, salt) * list.length) % list.length];
}

function domainSlugFor(i: number): string {
  return i < LAB_DOMAIN_SLUGS.length ? LAB_DOMAIN_SLUGS[i] : 'synthetic-' + i;
}

export function generateStressGraph(cfg: StressConfig): RexyGraphSnapshot {
  const nodes: RexyGraphNodeRecord[] = [];
  const edges: RexyGraphEdgeRecord[] = [];
  /** Leaves and systems, kept for deterministic relation wiring below. */
  const leaves: string[] = [];
  const systems: string[] = [];
  const agents: string[] = [];

  nodes.push({
    id: LAB_CITY_HALL_ID,
    label: 'REXY',
    kind: 'city-hall',
    trustTier: 'HIGHEST',
    importance: 1,
    layout: { pinned: true, x: 0, y: 0, z: 0 },
    metadata: { synthetic: true },
  });

  const contains = (source: string, target: string) => {
    edges.push({
      id: 'lab:contains:' + target,
      sourceId: source, targetId: target,
      kind: 'contains', directed: true,
      metadata: { synthetic: true },
    });
  };

  for (let d = 0; d < cfg.domainCount; d++) {
    const slug = domainSlugFor(d);
    const domainId = 'lab:domain:' + slug;

    nodes.push({
      id: domainId, label: slug.toUpperCase(), kind: 'domain',
      parentId: LAB_CITY_HALL_ID, domainId,
      trustTier: 'HIGHEST', importance: 0.85,
      layout: { ring: 0, order: d },
      metadata: { synthetic: true },
    });
    contains(LAB_CITY_HALL_ID, domainId);

    const agentCount = hashRange(domainId, 'agents', cfg.agentsPerDomain[0], cfg.agentsPerDomain[1]);

    for (let a = 0; a < agentCount; a++) {
      const agentId = 'lab:agent:' + slug + ':' + (a + 1);
      agents.push(agentId);
      nodes.push({
        id: agentId, label: slug.toUpperCase() + '-AGENT-' + (a + 1), kind: 'agent',
        parentId: domainId, domainId, agentId,
        trustTier: pick(TRUST_TIERS, agentId, 'trust'), importance: 0.68,
        metadata: { synthetic: true },
      });
      contains(domainId, agentId);

      // ── internal systems ──────────────────────────────────────────────────
      // Deterministically chosen kinds, and deliberately NOT the same set per
      // agent: a mansion's engines differ from its neighbour's.
      const systemCount = hashRange(agentId, 'systems', cfg.systemsPerAgent[0], cfg.systemsPerAgent[1]);
      for (let s = 0; s < systemCount; s++) {
        const sysId = 'lab:system:' + slug + ':' + (a + 1) + ':' + (s + 1);
        const kind = SYSTEM_KINDS[
          Math.floor(hashUnit(sysId, 'kind') * SYSTEM_KINDS.length) % SYSTEM_KINDS.length
        ];
        systems.push(sysId);
        nodes.push({
          id: sysId, label: kind.toUpperCase().replace('MEMORY-CLUSTER', 'MEMORY') + '-' + (s + 1),
          kind, parentId: agentId, domainId, agentId,
          trustTier: pick(TRUST_TIERS, sysId, 'trust'), importance: 0.42,
          metadata: { synthetic: true },
        });
        contains(agentId, sysId);
      }

      // ── memory clusters and their record clouds ───────────────────────────
      const clusterCount = hashRange(agentId, 'clusters', cfg.clustersPerAgent[0], cfg.clustersPerAgent[1]);
      for (let c = 0; c < clusterCount; c++) {
        const clusterId = 'lab:cluster:' + slug + ':' + (a + 1) + ':' + (c + 1);
        const recordCount = hashRange(
          clusterId, 'records', cfg.recordsPerCluster[0], cfg.recordsPerCluster[1],
        );

        nodes.push({
          id: clusterId, label: 'MEMORY-' + (c + 1), kind: 'memory-cluster',
          parentId: agentId, domainId, agentId,
          trustTier: pick(TRUST_TIERS, clusterId, 'trust'), importance: 0.34,
          childCount: recordCount,
          metadata: { synthetic: true },
        });
        contains(agentId, clusterId);

        for (let f = 0; f < recordCount; f++) {
          const n = String(f + 1);
          const fileId =
            'lab:file:' + slug + ':' + (a + 1) + ':' + (c + 1) + ':' +
            '0000'.slice(0, Math.max(0, 4 - n.length)) + n;
          leaves.push(fileId);
          nodes.push({
            id: fileId, label: 'record-' + n,
            kind: pick(LEAF_KINDS, fileId, 'kind'),
            parentId: clusterId, domainId, agentId,
            trustTier: pick(TRUST_TIERS, fileId, 'trust'),
            importance: 0.08 + hashUnit(fileId, 'imp') * 0.1,
            metadata: { synthetic: true },
          });
          contains(clusterId, fileId);
        }
      }
    }
  }

  // ── typed relations ─────────────────────────────────────────────────────────
  // Every relation connects two real records, carries a stable id and a kind, and
  // is chosen by hash. Most stay local — a record references a sibling or one of
  // its agent's systems — with a deliberately sparse minority reaching across
  // agents and across domains. That mix is what gives the galaxy interior
  // structure without turning it into an even hairball.
  const relationKinds = ['references', 'derived-from', 'reads', 'depends-on'] as const;
  let relationIndex = 0;

  const addRelation = (source: string, target: string, kind: string, crossDomain: boolean) => {
    if (source === target) return;
    edges.push({
      id: 'lab:rel:' + relationIndex++,
      sourceId: source, targetId: target,
      kind: kind as RexyGraphEdgeRecord['kind'],
      directed: true,
      crossDomain,
      approved: kind === 'approved-cross-domain',
      metadata: { synthetic: true },
    });
  };

  const nodeById: { [id: string]: RexyGraphNodeRecord } = {};
  for (let i = 0; i < nodes.length; i++) nodeById[nodes[i].id] = nodes[i];

  const whole = Math.floor(cfg.relationsPerLeaf);
  const fraction = cfg.relationsPerLeaf - whole;

  for (let i = 0; i < leaves.length; i++) {
    const id = leaves[i];
    const self = nodeById[id];
    const count = whole + (hashUnit(id, 'reln') < fraction ? 1 : 0);

    for (let r = 0; r < count; r++) {
      const roll = hashUnit(id, 'roll' + r);
      let target: string;
      let crossDomain = false;

      // DISTRIBUTION MATTERS MORE THAN COUNT. The first cut sent 9% of leaf
      // relations to any leaf graph-wide and 3% across domains. Those few
      // thousand edges span the entire galaxy, and additive sparks along them
      // drew bright streaks straight through the middle — the field read as a
      // brush stroke rather than a neural sphere. Long-range reach is now
      // genuinely sparse: the interior structure comes from local wiring.
      if (roll < 0.58) {
        // a sibling record in the same cluster
        const offset = 1 + Math.floor(hashUnit(id, 'sib' + r) * 24);
        target = leaves[(i + offset) % leaves.length];
      } else if (roll < 0.94 && systems.length) {
        // one of the agent's own internal systems
        target = systems[Math.floor(hashUnit(id, 'sys' + r) * systems.length) % systems.length];
      } else if (roll < 0.985) {
        // a record under a different agent — kept near in the id space so the
        // reach is real but not galaxy-spanning
        const near = 1 + Math.floor(hashUnit(id, 'far' + r) * 4000);
        target = leaves[(i + near) % leaves.length];
      } else {
        // sparse approved cross-domain reach
        target = agents.length
          ? agents[Math.floor(hashUnit(id, 'x' + r) * agents.length) % agents.length]
          : leaves[(i + 7) % leaves.length];
      }

      const other = nodeById[target];
      crossDomain = !!(self && other && self.domainId !== other.domainId);
      const kind = crossDomain && roll >= 0.985
        ? 'approved-cross-domain'
        : relationKinds[Math.floor(hashUnit(id, 'k' + r) * relationKinds.length) % relationKinds.length];
      addRelation(id, target, kind, crossDomain);
    }
  }

  // Agents depend on their own systems — the structural mid-tier relations that
  // make a mansion read as wired rather than as a hub with satellites.
  for (let i = 0; i < systems.length; i++) {
    const sys = nodeById[systems[i]];
    if (sys && sys.agentId) addRelation(sys.agentId, systems[i], 'uses', false);
  }

  // A handful of orphans on the outer shell. "Unattached" must be VISIBLE.
  for (let o = 0; o < 6; o++) {
    nodes.push({
      id: 'lab:orphan:' + (o + 1), label: 'ORPHAN-' + (o + 1), kind: 'unknown',
      orphan: true, trustTier: 'LOW', importance: 0.05,
      metadata: { synthetic: true },
    });
  }

  return { revision: 'lab-' + cfg.seed + '-' + nodes.length, nodes, edges };
}
